import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSessionActions } from './useSessionActions';
import { useSessionSubscriptions } from './useSessionSubscriptions';
import { useAnswerHandling } from './useAnswerHandling';
import { useQuestionNavigation } from './useQuestionNavigation';

/**
 * Main hook for managing live session state and functionality
 * @param {string} sessionId - Session ID
 * @param {boolean} isConsultant - Whether user is consultant
 * @returns {Object} Session state and functions
 */
export const useLiveSession = ({ sessionId, isConsultant = false }) => {
  const navigate = useNavigate();
  
  // Session state
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Action states
  const [endingSession, setEndingSession] = useState(false);
  const [leavingSession, setLeavingSession] = useState(false);
  const [completingSession, setCompletingSession] = useState(false);
  const [sessionSummary, setSessionSummary] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  
  // Answer submission states
  const [currentAnswers, setCurrentAnswers] = useState({});
  const [savingAnswers, setSavingAnswers] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  
  // Question navigation state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Initialize session actions
  const {
    fetchSession,
    handleEndSession,
    handleCancelSession,
    handleCompleteSession,
    fetchSessionSummary,
    handleLeaveSession,
    updateRefs
  } = useSessionActions({
    sessionId,
    setSession,
    setLoading,
    setError,
    setCurrentQuestionIndex,
    setCurrentAnswers,
    setEndingSession,
    setCompletingSession,
    setSessionSummary,
    setShowSummary
  });

  // Initialize answer handling
  const { handleAnswerChange, submitAnswer } = useAnswerHandling({
    sessionId,
    currentAnswers,
    setCurrentAnswers,
    savingAnswers,
    setSavingAnswers,
    saveTimeouts,
    setSaveTimeouts,
    fetchSession
  });

  // Initialize question navigation
  const { goToNextQuestion, goToPreviousQuestion, syncQuestionIndex, isNavigatingRef } = useQuestionNavigation({
    sessionId,
    session,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    currentAnswers,
    saveTimeouts,
    setSaveTimeouts,
    submitAnswer
  });

  // Initialize session subscriptions
  const { realtimeChannel } = useSessionSubscriptions({
    sessionId,
    session,
    isConsultant,
    setSession,
    setCurrentQuestionIndex,
    setCurrentAnswers,
    isNavigatingRef,
    fetchSession // Pass fetchSession for polling fallback
  });
  
  // Update refs in useSessionActions whenever state changes
  useEffect(() => {
    if (updateRefs) {
      updateRefs(currentAnswers, submitAnswer, currentQuestionIndex, session);
    }
  }, [currentAnswers, submitAnswer, currentQuestionIndex, session, updateRefs]);

  // Initialize session on mount
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Redirect if session is completed or cancelled
  useEffect(() => {
    // Use optional chaining to safely access session.status
    if (session?.status === 'completed' || session?.status === 'cancelled') {
      const message = session.status === 'completed' 
        ? 'This session has been completed' 
        : 'This session has been cancelled';
      
      toast(message, { icon: '📋', duration: 5000 });
      
      // Auto-redirect after 3 seconds
      const timer = setTimeout(() => {
        navigate(isConsultant ? '/consultant/dashboard' : '/dashboard');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [session, isConsultant, navigate]);

  return {
    // State
    session,
    loading,
    error,
    endingSession,
    leavingSession,
    completingSession,
    sessionSummary,
    showSummary,
    currentAnswers,
    savingAnswers,
    currentQuestionIndex,
    realtimeChannel,
    
    // Actions
    fetchSession,
    handleEndSession,
    handleCancelSession,
    handleCompleteSession,
    fetchSessionSummary,
    handleLeaveSession,
    handleAnswerChange,
    submitAnswer,
    goToNextQuestion,
    goToPreviousQuestion,
    syncQuestionIndex,
    setShowSummary
  };
};

